import React, { useEffect, useState } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import { DATE_TIME_FORMAT, Features, genericErrorToast, getMenuItemById, hasOrgPermission, hasPermission, passlyFormatDate } from '@app/helpers';
import { IModalData, ModalComponent, PageLoader, PasslySwitch } from '@app/components';
import { useSetPage, useTypedTranslation } from '@app/hooks';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { basicService, darkwebService, featureService } from '@app/services';
import { getModalSwitch, showOrgSelect } from '@app/store/actions';
import { ChooseVaultDialog } from '@app/pages/integrationManager/dialogs';
import { Button } from '@itglue/kaseya-ui';
import { DarkwebUsersTable } from '@app/pages/integrationManager/components';
import { Administrator, ManageTenant } from '@app/store/constants';
import { Link } from '@material-ui/core';
import { Intercom } from '@app/models';

export interface IReportData {
    $id: string;
    createdAt: string;
    id: string;
    organizationId: string;
    ranAt: string;
    status: string;
}

const DarkwebCompromisePage = () => {
    const { translation } = useTypedTranslation();
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.DarkwebCompromise);
    const [isCSVEnabled, setIsCSVEnabled] = useState(false);
    const [isBlockCompromisedEnabled, setIsBlockCompromisedEnabled] = useState(false);
    const [isShowLoader, setIsShowLoader] = useState(false);
    const dispatch = useDispatch();
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const currentOrgId = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const [reportId, setReportId] = useState('');
    const [isDarkwebUsersReportsEnabled, setIsDarkwebUsersReportsEnabled] = useState(false);
    const [disabledPasswordServerOrgName, setDisabledPasswordServerOrgName] = useState('');
    const [reportCreatedAt, setReportCreatedAt] = useState('');
    const [reportRanAt, setReportRanAt] = useState('');
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });
    const intercom: Intercom = useSelector((state: any) => state.basic.shellConfig.intercom);

    if (!intercom.dwidEnabled) {
        basicService.redirect('/integrations');
    }

    const handleEnableCSVSuccess = (response: AxiosResponse) => {
        const result: any = response.data;

        setIsShowLoader(false);

        if (result) {
            const snackbarMessage = result.enabled ? translation('KASEYA_ONE_IS_ENABLED') : translation('KASEYA_ONE_IS_DISABLED');

            setIsCSVEnabled(result.enabled);
            dispatch(handleSetSnackbar({ show: true, message: snackbarMessage, severity: 'success' }));
        }
    };

    const handleEnableScanningSuccess = (response: AxiosResponse) => {
        const result: any = response.data;

        setIsShowLoader(false);

        if (result) {
            const snackbarMessage = result.enabled ? translation('KASEYA_ONE_IS_ENABLED') : translation('KASEYA_ONE_IS_DISABLED');

            setIsCSVEnabled(result.enabled);
            dispatch(handleSetSnackbar({ show: true, message: snackbarMessage, severity: 'success' }));
        }
    };

    const handleBlockCompromisedSuccess = (response: AxiosResponse) => {
        setIsShowLoader(false);
        setIsBlockCompromisedEnabled(!isBlockCompromisedEnabled);
    };

    const handleError = (error: AxiosError<any>) => {
        setIsShowLoader(false);
        genericErrorToast(error);
    };

    const enableCSVChange = () => {
        darkwebService.changeCsvState(currentOrgId, !isCSVEnabled, handleError, handleEnableCSVSuccess);
        setIsCSVEnabled(!isCSVEnabled);
    };

    const openVaultsModal = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('DARKWEB_COMPROMISE_MODAL_TITLE'),
            modalSwitcher: modalSwitchState,
            form: <ChooseVaultDialog />
        });
    };

    const blockCompromised = () => {
        setIsShowLoader(true);
        darkwebService.blockCompromised(currentOrgId, !isBlockCompromisedEnabled, handleError, handleBlockCompromisedSuccess);
    };

    const redirectToLicenseAllocation = () => {
        basicService.redirect('/license/allocation');
    };

    const scanOrgCompromises = () => {
        darkwebService.scheduleCompromisesReportForOrg(
            currentOrgId,
            () => {},
            () => {
                getReports();
            }
        );
    };

    const handleSuccess = (result: AxiosResponse) => {
        if (result.data.isPasswordServerEnabled) {
            setIsBlockCompromisedEnabled(result.data.vaultPassword);
            setIsCSVEnabled(result.data.csvImport);
            setDisabledPasswordServerOrgName('');
        } else {
            setDisabledPasswordServerOrgName(result.data.organizationName);
        }
    };

    const getReports = () => {
        darkwebService.getReports(
            currentOrgId,
            () => {},
            (res: AxiosResponse) => {
                const reports: IReportData[] = res.data;
                let lastCreatedAt = '';
                let lastRanAt = '';

                reports.map((report) => {
                    const createdAtMS = Date.parse(report.createdAt);
                    const lastCreatedAtMS = lastCreatedAt ? Date.parse(lastCreatedAt) : 0;

                    if (report.status.toLowerCase() === 'scheduled' && createdAtMS > lastCreatedAtMS) {
                        lastCreatedAt = report.createdAt;
                    }

                    if (report.status.toLowerCase() === 'completed') {
                        lastRanAt = report.ranAt;
                        setReportId(report.id);
                    }
                });

                setReportRanAt(lastRanAt);
                setReportCreatedAt(lastCreatedAt);
            }
        );
    };

    useSetPage(null, item);

    const checkIsDarkwebUsersReportsEnabled = () => {
        featureService
            .checkFeature(Features.DarkwebUsersReports)
            .then((res) => {
                const isDarkwebReportsEnabled = res.data;

                setIsDarkwebUsersReportsEnabled(isDarkwebReportsEnabled);
            })
            .catch(() => {});
    };

    useEffect(() => {
        if (currentOrgId) {
            darkwebService.getFeaturesStatus(currentOrgId, handleError, handleSuccess);
        }
    }, [currentOrgId]);

    useEffect(() => {
        checkIsDarkwebUsersReportsEnabled();
        dispatch(showOrgSelect());
    }, []);

    useEffect(() => {
        if (isDarkwebUsersReportsEnabled && currentOrgId && (hasOrgPermission([Administrator]) || hasPermission([ManageTenant]))) {
            getReports();
        }
    }, [currentOrgId, isDarkwebUsersReportsEnabled]);

    return (
        <>
            <ModalComponent {...modalData} />
            <div className='darkweb-compromise'>
                <div className='content'>
                    <h1 className='header--h1'>{translation('DARKWEB_COMPROMISE_TITLE')}</h1>
                    {disabledPasswordServerOrgName ? (
                        <div className='darkweb-compromise__disablled'>
                            {translation('PASSWORD_SERVER_IS_DISABLED')}"
                            <Link underline='always' onClick={redirectToLicenseAllocation}>
                                {disabledPasswordServerOrgName}
                            </Link>
                            "
                        </div>
                    ) : (
                        <>
                            <div className='darkweb-compromise__switch-text'>
                                <div className='darkweb-compromise__button-description'>
                                    {translation('DARKWEB_COMPROMISE_BUTTON_ENABLE_SCANNING')}
                                </div>
                                <Button variant='primary' autoWidth={true} iconPlacement='before' icon='search' onClick={openVaultsModal}>
                                    {translation('DARKWEB_COMPROMISE_BUTTON_SCAN_NOW')}
                                </Button>
                            </div>
                            <div className='darkweb-compromise__switch-text'>
                                <PasslySwitch
                                    checked={isCSVEnabled}
                                    onChange={enableCSVChange}
                                    value={isCSVEnabled}
                                    label={translation('DARKWEB_COMPROMISE_BUTTON_ENABLE_CSV')}
                                />
                            </div>
                            <div className='darkweb-compromise__switch-text'>
                                <PasslySwitch
                                    checked={isBlockCompromisedEnabled}
                                    onChange={blockCompromised}
                                    value={isBlockCompromisedEnabled}
                                    label={translation('DARKWEB_COMPROMISE_BUTTON_BLOCK_COMPROMISED')}
                                />
                            </div>

                            {isDarkwebUsersReportsEnabled && (hasOrgPermission([Administrator]) || hasPermission([ManageTenant])) ? (
                                <>
                                    <div className='darkweb-compromise__users-compromise-select'>
                                        <Button
                                            className='darkweb-compromise__org-compromise-button'
                                            variant='primary'
                                            autoWidth={true}
                                            iconPlacement='before'
                                            icon='search'
                                            onClick={scanOrgCompromises}
                                        >
                                            {translation('DARKWEB_COMPROMISE_BUTTON_SCAN_FOR_ORG')}
                                        </Button>

                                        {reportRanAt && (
                                            <span className='darkweb-compromise__time'>{`${translation(
                                                'DARKWEB_COMPROMISE_REPORT_COMPLETED_AT'
                                            )}  ${passlyFormatDate(reportRanAt, DATE_TIME_FORMAT)}`}</span>
                                        )}
                                        {reportCreatedAt && (
                                            <span className='darkweb-compromise__time darkweb-compromise__time-grey'>{`${translation(
                                                'DARKWEB_COMPROMISE_REPORT_SCHEDULED_AT'
                                            )} ${passlyFormatDate(reportCreatedAt, DATE_TIME_FORMAT)}`}</span>
                                        )}
                                    </div>
                                    <div className='darkweb-compromise__users-compromise-table'>
                                        <DarkwebUsersTable reportId={reportId} />
                                    </div>
                                </>
                            ) : null}
                        </>
                    )}
                </div>
                {isShowLoader && <PageLoader useOverlay={true} />}
            </div>
        </>
    );
};

export default DarkwebCompromisePage;
